import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const userData = localStorage.getItem('user_data');
    if (token && userData) {
      // Tampered/corrupt localStorage must not crash the entire app on boot.
      try {
        setUser(JSON.parse(userData));
      } catch {
        // Bad entry — drop just the corrupt cache and stay signed out.
        localStorage.removeItem('user_data');
        setUser(null);
      }
    }
    setLoading(false);

    // Account switched in ANOTHER tab (localStorage is shared): a tab still
    // showing the previous user's data would keep rendering it while new
    // API calls silently use the new identity. Reload so every open tab
    // re-fetches as exactly one user.
    const onStorage = (e) => {
      if (e.key === 'user_data' || e.key === 'access_token' || e.key === null) {
        window.location.reload();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = async (email, password) => {
    // Login endpoint returns the TokenResponse directly (no {success, data} envelope),
    // unlike the other /auth/* endpoints. Do NOT destructure .data here.
    const resp = await authAPI.login(email, password);
    const data = resp?.data ?? resp; // tolerate either shape
    // SECURITY TODO (audit C1): access/refresh tokens are stored in
    // localStorage, which is readable by any XSS payload. Migrate to
    // httpOnly + Secure + SameSite cookies set by the backend on
    // login/refresh (requires coordinated backend changes).
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    localStorage.setItem('user_data', JSON.stringify({ party_id: data.party_id, name: data.name, roles: data.roles }));
    setUser({ party_id: data.party_id, name: data.name, roles: data.roles });
    return data;
  };

  const logout = () => {
    // Best-effort server-side revocation; capture the token BEFORE clearing
    // storage so the request can still authenticate. Local sign-out never waits.
    const token = localStorage.getItem('access_token');
    localStorage.clear();
    setUser(null);
    if (token) authAPI.logoutWithToken(token).catch(() => {});
  };

  const hasRole = (role) => user?.roles?.includes(role);

  // True if the user has ANY of the supplied roles. Accepts either a
  // list: hasAny(['ADMIN','DOCTOR']) or a spread: hasAny('ADMIN','DOCTOR').
  const hasAny = (...roles) => {
    const flat = roles.flat();
    if (!user?.roles) return false;
    return flat.some((r) => user.roles.includes(r));
  };

  const isAdmin = () => hasRole('ADMIN');

  return (
    <AuthContext.Provider value={{ user, login, logout, hasRole, hasAny, isAdmin, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
